import { Injectable } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';

import { GlobalDashboardRepository } from '@app/domain/ports/global-dashboard.repository';
import {
  GlobalBalance,
  GlobalDashboardQuery,
  GlobalDashboardSummary,
  GlobalSeriesPoint,
} from '@app/domain/models/global-dashboard.model';
import {
  GlobalDashboardForbiddenError,
  GlobalDashboardUnavailableError,
} from '@app/domain/models/global-dashboard.errors';

/**
 * Escenarios del Saldo global que el mock reproduce (feature 108). Se cambia la
 * constante `GLOBAL_DASHBOARD_MOCK_SCENARIO` para ver cada uno sin backend.
 */
export type GlobalDashboardMockScenario =
  | 'complete'
  | 'partialWithoutCards'
  | 'partialWithoutSpei'
  | 'absent'
  | 'speiOnly'
  | 'forbidden'
  | 'unavailable';

export const GLOBAL_DASHBOARD_MOCK_SCENARIO: GlobalDashboardMockScenario = 'complete';

/** El saldo de banca (contrato C4) por escenario, ya en modelo de dominio. */
export function mockGlobalBalance(scenario: GlobalDashboardMockScenario): GlobalBalance {
  switch (scenario) {
    case 'partialWithoutCards':
      return { total: 900000, status: 'partial', spei: { amount: 900000, status: 'included' }, cards: { amount: null, status: 'absent' } };
    case 'partialWithoutSpei':
      return { total: 350430.55, status: 'partial', spei: { amount: null, status: 'absent' }, cards: { amount: 350430.55, status: 'included' } };
    case 'speiOnly':
      return { total: 900000, status: 'complete', spei: { amount: 900000, status: 'included' }, cards: { amount: null, status: 'not_applicable' } };
    case 'absent':
    case 'forbidden':
    case 'unavailable':
      return { total: null, status: 'absent', spei: { amount: null, status: 'absent' }, cards: { amount: null, status: 'absent' } };
    case 'complete':
    default:
      return { total: 1250430.55, status: 'complete', spei: { amount: 900000, status: 'included' }, cards: { amount: 350430.55, status: 'included' } };
  }
}

/**
 * Mock del Dashboard Global para desarrollo sin backend
 * (`environment.globalDashboardUseMocks`).
 *
 * Simula el contrato de `GET /api/v1/dashboard/global-summary/` con un usuario
 * de 3 líneas de negocio. Conceptos (redefinidos 2026-07-02):
 *   transfers = TODO SPEI (individuales + masivas) · cards = Dispersiones/Tarjetas
 *   · tpv = TPV. Las líneas futuras (QR, links, e-commerce) NO vienen en
 * `availableServices`, igual que hará el backend, para validar que el layout
 * se acopla a los servicios reales.
 */
@Injectable()
export class GlobalDashboardMockDataSource extends GlobalDashboardRepository {
  /** Público para que el spec recorra los escenarios sin tocar la constante. */
  scenario: GlobalDashboardMockScenario = GLOBAL_DASHBOARD_MOCK_SCENARIO;

  override getSummary(query: GlobalDashboardQuery): Observable<GlobalDashboardSummary> {
    if (this.scenario === 'forbidden' || this.scenario === 'unavailable') {
      const error = this.scenario === 'forbidden' ? new GlobalDashboardForbiddenError() : new GlobalDashboardUnavailableError();
      // Mismo delay que el camino feliz: el error llega tras la "red", no al suscribirse.
      return timer(300).pipe(switchMap(() => throwError(() => error)));
    }
    return of(this.buildSummary(query)).pipe(delay(300));
  }

  private buildSummary(query: GlobalDashboardQuery): GlobalDashboardSummary {
    const months = ['2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06'];
    const series = (values: number[]): GlobalSeriesPoint[] =>
      months.map((bucket, i) => ({ bucket, value: values[i] ?? 0 }));

    return {
      periodLabel: this.periodLabel(query),
      balance: mockGlobalBalance(this.scenario),
      currency: 'MXN',
      availableServices: ['transfers', 'cards', 'tpv'],
      seriesGranularity: 'month',
      lineCards: [
        {
          lineId: 'spei',
          ingresosCount: 28,
          ingresosAmount: 432895,
          egresosCount: 28,
          egresosAmount: 152284,
          currency: 'MXN',
        },
        {
          lineId: 'cards',
          ingresosCount: 12,
          ingresosAmount: 98420,
          egresosCount: 15,
          egresosAmount: 61200.75,
          currency: 'MXN',
        },
        {
          lineId: 'tpv',
          ingresosCount: 19,
          ingresosAmount: 250311.5,
          egresosCount: 6,
          egresosAmount: 48090.25,
          currency: 'MXN',
        },
      ],
      series: [
        { serviceId: 'transfers', points: series([41, 12, 23, 18, 31, 14]) },
        { serviceId: 'cards', points: series([2, 6, 11, 4, 9, 14]) },
        { serviceId: 'tpv', points: series([5, 12, 3, 9, 16, 8]) },
      ],
      incomeExpense: [
        { serviceId: 'transfers', ingresos: 432895, egresos: 251034.4 },
        { serviceId: 'cards', ingresos: 98420, egresos: 61200.75 },
        { serviceId: 'tpv', ingresos: 250311.5, egresos: 48090.25 },
      ],
      serviceUsage: [
        { serviceId: 'transfers', count: 139, percentage: 63.8 },
        { serviceId: 'cards', count: 26, percentage: 11.9 },
        { serviceId: 'tpv', count: 53, percentage: 24.3 },
      ],
      recentActivity: [
        {
          id: 'mock-1',
          serviceId: 'transfers',
          direction: 'IN',
          amount: 2000,
          currency: 'MXN',
          ordenante: '*****7181',
          beneficiario: '*****1213',
          realizador: '',
          clientName: 'Diego Montoya',
          reference: 'Shopify Express',
          occurredAt: '2026-07-02T14:15:00Z',
          relativeDate: '',
        },
        {
          id: 'mock-2',
          serviceId: 'transfers',
          direction: 'OUT',
          amount: 9000,
          currency: 'MXN',
          ordenante: '*****2233',
          beneficiario: '*****1415',
          realizador: 'patricia.miller@example.com',
          clientName: 'Sofia Ramirez',
          reference: 'MarketPlace Hub',
          occurredAt: '2026-07-02T09:15:00Z',
          relativeDate: '',
        },
        {
          id: 'mock-3',
          serviceId: 'cards',
          direction: 'OUT',
          amount: 800,
          currency: 'MXN',
          ordenante: '*****5161',
          beneficiario: '*****9202',
          realizador: 'robert.davis@example.com',
          clientName: 'Carlos Mendoza',
          reference: 'BuyItNow Store',
          occurredAt: '2026-07-02T09:15:00Z',
          relativeDate: '',
        },
        {
          id: 'mock-4',
          serviceId: 'cards',
          direction: 'IN',
          amount: 512,
          currency: 'MXN',
          ordenante: '*****3141',
          beneficiario: '*****1234',
          realizador: '',
          clientName: 'Isabella Torres',
          reference: 'QuickCart',
          occurredAt: '2026-07-01T12:00:00Z',
          relativeDate: '',
        },
        {
          id: 'mock-5',
          serviceId: 'tpv',
          direction: 'IN',
          amount: 129,
          currency: 'MXN',
          ordenante: '*****1121',
          beneficiario: '*****0588',
          realizador: 'michael.brown@example.com',
          clientName: 'Javier Ortega',
          reference: 'Ecom Haven',
          occurredAt: '2026-06-27T10:30:00Z',
          relativeDate: '',
        },
        {
          id: 'mock-6',
          serviceId: 'tpv',
          direction: 'OUT',
          amount: 250,
          currency: 'MXN',
          ordenante: '*****9101',
          beneficiario: '*****1011',
          realizador: 'sarah.jones@example.com',
          clientName: 'Lucia Gonzalez',
          reference: 'Retail Rocket',
          occurredAt: '2026-06-12T16:45:00Z',
          relativeDate: '',
        },
      ],
    };
  }

  /** Etiqueta legible del período, como la calculará el backend. */
  private periodLabel(query: GlobalDashboardQuery): string {
    switch (query.period) {
      case 'hoy': return 'Hoy';
      case 'semana': return 'Esta semana';
      case 'mes': return 'Este mes';
      case 'año': return 'Este año';
      case 'custom': return `${query.dateFrom ?? ''} — ${query.dateTo ?? ''}`;
      default: return '';
    }
  }
}
